import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Radar, Layers3, Gauge, ServerCog, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "./primitives";
import { CornerFrame } from "./CornerFrame";
import { Magnetic } from "./Magnetic";
import { WaitlistModal } from "./WaitlistModal";

const POINTS = [
  {
    icon: Layers3,
    title: "Tiered classification",
    body: "Every asset scored STD · STRONG · PREMIUM · SCALP — pattern strength as a structured read of how and where confluence formed, not one flattened number. Paired with live bias, volatility and heat on every row.",
  },
  {
    icon: Gauge,
    title: "Market-wide regime layer",
    body: "A live Fear/Greed-style pulse reads aggregate bullish/bearish posture across the entire scanned universe — so you never evaluate a single setup in a vacuum.",
  },
  {
    icon: ServerCog,
    title: "Runs on our infrastructure",
    body: "Not a static script frozen on your chart. The engine lives on our servers and data pipeline — continuously ingesting live data and recalibrated against real outcomes. It runs whether your chart is open or not.",
  },
  {
    icon: Radar,
    title: "Same logic, whole market",
    body: "The same core detection behind our TradingView tools, scaled from one chart to 800+ assets — in real time, continuously, with zero manual scanning required.",
  },
];

export const SignalScanner = () => {
  const [open, setOpen] = useState(false);

  return (
    <section
      id="signal-scanner"
      data-testid="signal-scanner"
      className="relative border-y border-white/[0.10] bg-[#070707] overflow-hidden"
    >
      {/* ambient */}
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 h-72 w-[900px] bg-white/[0.03] blur-[130px]" />
      <div className="pointer-events-none absolute inset-0 tech-grid opacity-60" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-28">
        {/* header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-3xl">
            <Reveal>
              <div className="flex items-center gap-3 flex-wrap">
                <SectionLabel index="CORE">The Scanner</SectionLabel>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="mt-6 inline-flex items-center gap-2 border border-[#BACD1C] bg-white/[0.03] px-3 py-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#BACD1C] opacity-70" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#BACD1C]" />
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B4BAC4]">
                  Coming soon · Founder early release
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display font-semibold tracking-[-0.03em] text-3xl sm:text-4xl lg:text-6xl leading-[0.95]">
                Built like a quant desk.
                <span className="block text-[#BACD1C]">Run like one too.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 text-sm sm:text-base text-white/55 leading-relaxed">
                Our flagship. Not a screener bolted onto a price feed — a live confluence-detection
                pipeline running continuously across <span className="text-white/85">800+ crypto assets</span>,
                cross-referencing price structure, volume and momentum across multiple timeframes to
                surface patterns <span className="text-[#7D8390]">as they form — not after they've moved.</span>
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="shrink-0">
            <Magnetic strength={0.35}>
              <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="scanner-waitlist-button"
                className="group inline-flex items-center gap-2.5 bg-[#BACD1C] text-black font-mono text-xs uppercase tracking-[0.2em] px-6 py-4 hover:bg-white active:scale-[0.98] transition-[background-color,transform] duration-300"
              >
                Create free account
                <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform duration-300" />
              </button>
            </Magnetic>
          </Reveal>
        </div>

        {/* flagship video — clean, no chrome, no dimming */}
        <Reveal delay={0.1}>
          <div className="mt-10 mx-auto max-w-4xl relative">
            <div className="pointer-events-none absolute -inset-6 bg-[#BACD1C]/[0.10] blur-3xl" />
            <div className="relative border border-white/[0.10] overflow-hidden bg-black shadow-[0_40px_100px_-30px_rgba(0,0,0,0.9)]">
              <CornerFrame />
              <div className="flex items-center gap-2 border-b border-white/[0.10] bg-[#0c0c0c] px-3.5 py-2.5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#BACD1C]/80" />
                <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 truncate">scanner.live — 800 assets · multi-timeframe</span>
                <span className="ml-auto flex items-center gap-1.5 shrink-0">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#BACD1C] opacity-60" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#BACD1C]" />
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#B4BAC4]">connected</span>
                </span>
              </div>
              <video
                src="/scanner.mp4"
                poster="/scanner-poster.jpg"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                onLoadedData={(e) => { e.currentTarget.play().catch(() => {}); }}
                onCanPlay={(e) => { e.currentTarget.play().catch(() => {}); }}
                data-testid="scanner-video"
                className="w-full h-auto block select-none"
              />
            </div>
          </div>
        </Reveal>

        {/* differentiator grid */}
        <div className="mt-14 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {POINTS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.05}>
              <motion.div
                whileHover={{ y: -3 }}
                transition={{ duration: 0.3 }}
                className="group h-full bg-[#0A0A0A] hover:bg-[#0E0E0E] p-6 sm:p-7 transition-colors duration-300"
                data-testid={`scanner-point-${i}`}
              >
                <div className="inline-flex h-10 w-10 items-center justify-center border border-white/[0.10] bg-white/[0.03] text-[#BACD1C] group-hover:bg-[#BACD1C] group-hover:text-black transition-colors duration-300">
                  <p.icon size={17} />
                </div>
                <h3 className="mt-4 font-display font-semibold tracking-[-0.03em] text-lg leading-tight">{p.title}</h3>
                <p className="mt-2.5 text-[13px] text-white/50 leading-relaxed">{p.body}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>

        {/* closing band */}
        <Reveal delay={0.1}>
          <div className="mt-14 border border-white/[0.10] bg-[#0E0E0E] p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute inset-0 lime-glow opacity-30" />
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
              <p className="font-display font-semibold tracking-[-0.03em] text-2xl sm:text-3xl lg:text-4xl leading-tight max-w-2xl">
                Professional-grade detection. Retail-accessible.
                <span className="text-[#7D8390]"> Built to watch everything you can't.</span>
              </p>
              <div className="shrink-0">
                <Magnetic strength={0.3}>
                  <button
                    type="button"
                    onClick={() => setOpen(true)}
                    data-testid="scanner-waitlist-button-bottom"
                    className="group inline-flex items-center gap-2.5 border border-[#BACD1C] text-[#B4BAC4] font-mono text-xs uppercase tracking-[0.2em] px-6 py-4 hover:bg-[#BACD1C] hover:text-black active:scale-[0.98] transition-[background-color,color,transform] duration-300"
                  >
                    Create free account
                    <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform duration-300" />
                  </button>
                </Magnetic>
                <p className="mt-3 font-mono text-[10px] text-white/30 tracking-[0.15em] uppercase text-center">
                  Coming soon · no card · one email at launch
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {open && <WaitlistModal open={open} onClose={() => setOpen(false)} />}
      </AnimatePresence>
    </section>
  );
};
